import React, { useState, useRef, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  Calendar, 
  Clock, 
  Target, 
  BookOpen, 
  Printer,
  FileText,
  CheckCircle2,
  Loader2,
  Sparkles,
  Share2,
  NotebookPen,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

// Import ICT weekly lessons (other subjects will use generic generation)
import { year11ICTWeeklyLessons, year12ICTWeeklyLessons, year13ICTWeeklyLessons } from '../../data/ictWeeklyLessons';

const LessonPlanGenerator = ({ 
  defaultYear = 11, 
  subject = 'ICT',
  subjectColor = 'green',
  allowedYears = ['11', '12', '13'],
  weeklyLessons = null // New prop for weekly lessons data
}) => {
  const navigate = useNavigate();
  const [selectedYear, setSelectedYear] = useState(defaultYear.toString());
  const [selectedTerm, setSelectedTerm] = useState('term1');
  const [selectedWeek, setSelectedWeek] = useState('1');
  const [selectedDuration, setSelectedDuration] = useState('60');
  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSharingToAcademic, setIsSharingToAcademic] = useState(false);

  // Study notes state
  const [includeStudyNotes, setIncludeStudyNotes] = useState(false);
  const [notesProgress, setNotesProgress] = useState({}); // { Monday: { status, noteId, jobId }, ... }
  const [generatingNotes, setGeneratingNotes] = useState(false);
  const cancelledRef = useRef(false);

  // Subject-specific topics for lesson generation
  const subjectTopics = {
    'ICT': {
      11: ['Computer Systems', 'Software Applications', 'Digital Graphics', 'Networks', 'Database Concepts', 'Web Development', 'Digital Citizenship', 'Problem Solving', 'Programming Basics', 'Project Work'],
      12: ['Advanced Hardware', 'Operating Systems', 'Advanced Applications', 'Network Security', 'Database Design', 'Web Programming', 'Multimedia', 'Systems Analysis', 'Programming', 'Major Project'],
      13: ['IT Management', 'Advanced Networks', 'Database Systems', 'Software Development', 'Web Technologies', 'Digital Media', 'E-Commerce', 'Project Management', 'Advanced Programming', 'Final Project']
    },
    'Chemistry': {
      11: ['Atomic Structure', 'Electron Configuration', 'Periodic Table', 'Chemical Bonding', 'Ionic Compounds', 'Covalent Compounds', 'Mole Concept', 'Chemical Equations', 'Stoichiometry', 'Acids and Bases'],
      12: ['Thermochemistry', 'Reaction Rates', 'Equilibrium', 'Acids and Bases II', 'Redox Reactions', 'Electrochemistry', 'Organic Chemistry Intro', 'Hydrocarbons', 'Functional Groups', 'Polymers'],
      13: ['Advanced Organic', 'Spectroscopy', 'Reaction Mechanisms', 'Industrial Chemistry', 'Biochemistry', 'Environmental Chemistry', 'Analytical Techniques', 'Research Methods', 'Extended Investigation', 'Exam Preparation']
    },
    'Physics': {
      11: ['Measurement', 'Kinematics', 'Forces', 'Energy', 'Momentum', 'Waves', 'Sound', 'Light', 'Electricity Basics', 'Circuits'],
      12: ['Advanced Mechanics', 'Circular Motion', 'Gravitation', 'Thermodynamics', 'Electric Fields', 'Magnetic Fields', 'Electromagnetic Induction', 'AC Circuits', 'Nuclear Physics', 'Modern Physics'],
      13: ['Quantum Physics', 'Special Relativity', 'Particle Physics', 'Astrophysics', 'Medical Physics', 'Advanced Electromagnetism', 'Research Project', 'Problem Solving', 'Exam Techniques', 'Final Review']
    },
    'Biology': {
      11: ['Cell Structure', 'Cell Transport', 'Enzymes', 'Molecular Genetics', 'Cell Division', 'Plant Reproduction', 'Human Nutrition', 'Ecosystems', 'Classification', 'Natural Selection'],
      12: ['Respiration', 'Photosynthesis', 'Cellular Immunity', 'Mendelian Inheritance', 'Circulatory Systems', 'Excretion', 'Population Ecology', 'Communities', 'Evolution Patterns', 'Speciation'],
      13: ['DNA Replication', 'Protein Synthesis', 'Biotechnology', 'Animal Behaviour', 'Biological Rhythms', 'Climate Change', 'Conservation', 'Gene Pools', 'Research Project', 'Exam Preparation']
    },
    'Agriculture': {
      11: ['Sustainable Farming', 'Soil Types', 'Soil Testing', 'Crop Production', 'Animal Production', 'Pest Management', 'Farm Economics', 'Marketing', 'Farm Safety', 'Project Planning'],
      12: ['Advanced Soil Science', 'Irrigation', 'Plant Nutrition', 'Livestock Management', 'Disease Control', 'Organic Farming', 'Agribusiness', 'Value Adding', 'Farm Technology', 'Enterprise Project'],
      13: ['Agricultural Systems', 'Climate Adaptation', 'Export Markets', 'Food Security', 'Research Methods', 'Farm Management', 'Sustainability', 'Policy and Planning', 'Major Project', 'Final Assessment']
    },
    'Mathematics': {
      7: ['Number Systems', 'Basic Operations', 'Fractions', 'Decimals', 'Percentages', 'Ratios', 'Basic Algebra', 'Geometry Intro', 'Data Handling', 'Problem Solving'],
      8: ['Integers', 'Algebraic Expressions', 'Linear Equations', 'Geometry', 'Area and Perimeter', 'Statistics', 'Probability Intro', 'Transformations', 'Coordinates', 'Review'],
      9: ['Advanced Algebra', 'Linear Functions', 'Quadratics Intro', 'Trigonometry Basics', 'Circle Properties', 'Surface Area', 'Volume', 'Data Analysis', 'Probability', 'Exam Prep'],
      10: ['Polynomials', 'Quadratic Functions', 'Trigonometry', 'Geometry Proofs', 'Statistics', 'Financial Mathematics', 'Measurement', 'Problem Solving', 'Investigation', 'Revision'],
      11: ['Functions', 'Quadratics', 'Indices and Logarithms', 'Trigonometry', 'Sequences', 'Differentiation Intro', 'Geometry', 'Statistics', 'Probability', 'Applications'],
      12: ['Advanced Functions', 'Calculus', 'Trigonometric Functions', 'Exponentials', 'Vectors', 'Complex Numbers', 'Statistics', 'Probability Distributions', 'Mathematical Modeling', 'Exam Techniques'],
      13: ['Advanced Calculus', 'Integration', 'Differential Equations', 'Matrices', 'Probability Theory', 'Statistical Inference', 'Optimization', 'Research Project', 'Problem Solving', 'Final Preparation']
    },
    'Economics': {
      11: ['Economic Concepts', 'Supply and Demand', 'Market Structures', 'Consumer Behaviour', 'Business Economics', 'National Income', 'Government Policy', 'Trade', 'Development', 'Pacific Economics'],
      12: ['Microeconomics', 'Production Theory', 'Cost Analysis', 'Market Failure', 'Macroeconomics', 'Monetary Policy', 'Fiscal Policy', 'International Trade', 'Development Economics', 'Case Studies'],
      13: ['Advanced Micro', 'Advanced Macro', 'Economic Growth', 'Globalisation', 'Environmental Economics', 'Labour Economics', 'Public Finance', 'Research Methods', 'Extended Essay', 'Exam Preparation']
    },
    'French': {
      11: ['Greetings and Introductions', 'Family', 'Daily Routine', 'School Life', 'Food and Drink', 'Shopping', 'Directions', 'Weather', 'Hobbies', 'French Culture'],
      12: ['Travel', 'Accommodation', 'Health', 'Media', 'Environment', 'Technology', 'Work', 'Future Plans', 'French Society', 'Literature Intro'],
      13: ['Current Affairs', 'Social Issues', 'Arts and Culture', 'History', 'Advanced Grammar', 'Essay Writing', 'Oral Presentation', 'Literature Study', 'Exam Techniques', 'Final Preparation']
    },
    'Geography': {
      11: ['Map Skills', 'Physical Geography', 'Weather and Climate', 'Ecosystems', 'Population', 'Settlement', 'Agriculture', 'Industry', 'Tourism', 'Pacific Geography'],
      12: ['Tectonics', 'Coastal Processes', 'River Systems', 'Atmospheric Systems', 'Urban Geography', 'Economic Activities', 'Development', 'Globalisation', 'Fieldwork', 'Case Studies'],
      13: ['Advanced Physical', 'Advanced Human', 'Environmental Management', 'Sustainability', 'Geographic Research', 'GIS Applications', 'Extended Investigation', 'Synoptic Assessment', 'Exam Skills', 'Revision']
    }
  };

  // Get curriculum data from weeklyLessons prop or ICT default
  const getWeeklyLessonsData = (year, term) => {
    // If weeklyLessons prop is provided, use it
    if (weeklyLessons) {
      const yearKey = `year${year}`;
      return weeklyLessons[yearKey]?.[term]?.scheme || weeklyLessons[yearKey]?.[term]?.weeks || [];
    }
    
    // Fallback to ICT if subject is ICT
    if (subject === 'ICT') {
      const ictData = getICTCurriculumData(year);
      return ictData?.[term]?.scheme || [];
    }
    
    return [];
  };

  // Get topic for the selected week - uses weekly lessons data if available
  const getWeekTopic = (year, week, term) => {
    const weeksData = getWeeklyLessonsData(year, term);
    
    if (weeksData.length > 0) {
      const weekData = weeksData.find(w => w.week === parseInt(week)) || weeksData[0];
      return weekData?.topic || `Week ${week} Topic`;
    }
    
    // Fallback to generic topics
    const yearTopics = subjectTopics[subject]?.[parseInt(year)] || subjectTopics['ICT'][11];
    const weekIndex = (parseInt(week) - 1) % yearTopics.length;
    return yearTopics[weekIndex];
  };

  // Get week data for detailed lesson generation
  const getWeekData = (year, week, term) => {
    const weeksData = getWeeklyLessonsData(year, term);
    if (weeksData.length > 0) {
      return weeksData.find(w => w.week === parseInt(week)) || weeksData[0];
    }
    return null;
  };

  // Get ICT curriculum data if subject is ICT
  const getICTCurriculumData = (year) => {
    switch(parseInt(year)) {
      case 11: return year11ICTWeeklyLessons;
      case 12: return year12ICTWeeklyLessons;
      case 13: return year13ICTWeeklyLessons;
      default: return year11ICTWeeklyLessons;
    }
  };

  // Generate lesson content for a specific day
  const generateDayLesson = (dayName, dayNumber, topic, learningOutcomes, weekData) => {
    const totalMin = parseInt(selectedDuration) || 60;
    const dayFocus = {
      'Monday': { focus: 'Introduction and Foundation', activity: 'introducing new concepts' },
      'Tuesday': { focus: 'Exploration and Practice', activity: 'hands-on exploration' },
      'Wednesday': { focus: 'Deep Dive and Application', activity: 'applying concepts' },
      'Thursday': { focus: 'Review and Reinforcement', activity: 'reviewing and practicing' },
      'Friday': { focus: 'Assessment and Extension', activity: 'assessment and wrap-up' }
    };

    const dayInfo = dayFocus[dayName] || dayFocus['Monday'];
    const dayOutcome = learningOutcomes?.[Math.min(dayNumber - 1, learningOutcomes.length - 1)] || `Understand ${topic}`;

    // Scale activity durations proportionally to selected total duration
    const scale = (baseMin) => Math.max(5, Math.round(baseMin * totalMin / 60));

    let activities = [];
    let intro = '';
    let materials = weekData?.resources ? 
      (Array.isArray(weekData.resources) ? weekData.resources.join(', ') : weekData.resources) : 
      `${subject} resources, whiteboard, student notebooks`;
    let assessment = '';
    let homework = '';

    const strandInfo = weekData?.strand ? `(${weekData.strand})` : '';

    if (dayName === 'Monday') {
      intro = `Welcome to our new topic: ${topic}${strandInfo ? ' ' + strandInfo : ''}! Today we begin exploring this important ${subject} concept.`;
      const a1 = scale(10), a2 = scale(20), a4 = scale(5);
      const a3 = totalMin - a1 - a2 - a4;
      activities = [
        { name: 'Opening Discussion', duration: `${a1} min`, description: `Discuss what students already know about ${topic}.` },
        { name: 'Teacher Presentation', duration: `${a2} min`, description: `Introduce key concepts of ${topic} with examples.` },
        { name: 'Guided Practice', duration: `${a3} min`, description: 'Students follow along with teacher demonstration.' },
        { name: 'Reflection', duration: `${a4} min`, description: 'Students write 3 things they learned today.' }
      ];
      assessment = weekData?.assessment || 'Observe participation and check understanding through Q&A';
      homework = `Research and write 5 facts about ${topic}`;
    } else if (dayName === 'Tuesday') {
      intro = `Today we continue exploring ${topic} with hands-on activities.`;
      const weekActivities = weekData?.activities || [];
      const a1 = scale(10), a4 = scale(5);
      const a2 = Math.round((totalMin - a1 - a4) * 0.55);
      const a3 = totalMin - a1 - a2 - a4;
      activities = [
        { name: 'Quick Review', duration: `${a1} min`, description: "Review Monday's lesson with quick quiz questions." },
        { name: 'Skill Building', duration: `${a2} min`, description: weekActivities[0] || `Practice key ${topic} skills with exercises.` },
        { name: 'Partner Work', duration: `${a3} min`, description: weekActivities[1] || 'Work in pairs to complete a practice task.' },
        { name: 'Share Results', duration: `${a4} min`, description: 'Pairs share their work with the class.' }
      ];
      assessment = 'Check completed partner work for understanding';
      homework = `Complete practice worksheet on ${topic}`;
    } else if (dayName === 'Wednesday') {
      intro = `Today we apply our knowledge of ${topic} to real-world scenarios.`;
      const weekActivities = weekData?.activities || [];
      const a1 = scale(10), a3 = scale(15), a4 = scale(5);
      const a2 = totalMin - a1 - a3 - a4;
      activities = [
        { name: 'Warm-up Activity', duration: `${a1} min`, description: 'Quick practical exercise to reinforce learning.' },
        { name: 'Application Project', duration: `${a2} min`, description: weekActivities[2] || `Apply ${topic} concepts to create a small project.` },
        { name: 'Troubleshooting', duration: `${a3} min`, description: 'Address common problems and questions.' },
        { name: 'Progress Check', duration: `${a4} min`, description: 'Students rate their confidence level 1-5.' }
      ];
      assessment = 'Review application project progress';
      homework = `Continue working on your ${topic} project`;
    } else if (dayName === 'Thursday') {
      intro = `Today we review and strengthen our understanding of ${topic}.`;
      const a1 = scale(15), a4 = scale(5);
      const a2 = Math.round((totalMin - a1 - a4) * 0.6);
      const a3 = totalMin - a1 - a2 - a4;
      activities = [
        { name: 'Review Game', duration: `${a1} min`, description: `Play a review game covering ${topic} concepts.` },
        { name: 'Practice Stations', duration: `${a2} min`, description: 'Rotate through different practice stations.' },
        { name: 'Q&A Session', duration: `${a3} min`, description: 'Address remaining questions and clarify confusions.' },
        { name: 'Self-Assessment', duration: `${a4} min`, description: 'Students complete self-assessment checklist.' }
      ];
      assessment = 'Observe station work and review self-assessments';
      homework = `Prepare for tomorrow's assessment - review all notes on ${topic}`;
    } else if (dayName === 'Friday') {
      intro = `Today we assess our learning of ${topic}. Show what you have learned!`;
      const a1 = scale(15), a4 = scale(5);
      const a2 = Math.round((totalMin - a1 - a4) * 0.6);
      const a3 = totalMin - a1 - a2 - a4;
      activities = [
        { name: 'Mini Quiz', duration: `${a1} min`, description: `Complete short quiz on ${topic} concepts.` },
        { name: 'Practical Task', duration: `${a2} min`, description: 'Complete a practical assessment task.' },
        { name: 'Extension Activity', duration: `${a3} min`, description: 'Early finishers explore extension challenges.' },
        { name: 'Week Wrap-up', duration: `${a4} min`, description: 'Celebrate achievements and preview next week.' }
      ];
      assessment = weekData?.assessment || 'Quiz results and practical task evaluation';
      homework = `Optional: Research how ${topic} is used in different careers`;
    }

    return {
      day: dayName,
      title: `${dayInfo.focus}: ${topic}`,
      duration: `${totalMin} min`,
      objectives: [dayOutcome, `Practice ${dayInfo.activity}`],
      introduction: intro,
      activities: activities,
      materials: materials,
      assessment: assessment,
      homework: homework,
      strand: weekData?.strand || ''
    };
  };

  // ─── Study Notes Generation ──────────────────────────────────────
  const generateStudyNotesForDays = useCallback(async (days, yearLevel) => {
    const user = JSON.parse(localStorage.getItem('currentUser') || localStorage.getItem('user') || '{}');
    if (!user?.id) {
      toast.error('Please log in to generate study notes');
      return;
    }

    setGeneratingNotes(true);
    cancelledRef.current = false;
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');

    for (const day of days) {
      if (cancelledRef.current) break;

      const dayTopic = `${day.day}: ${day.title.replace(/^[^:]+:\s*/, '')}`;
      setNotesProgress(prev => ({ ...prev, [day.day]: { status: 'generating', progress: 'Starting...' } }));

      try {
        // Start generation
        const res = await fetch(`/api/study-notes/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            subject: subject.toLowerCase(),
            year_level: yearLevel,
            topic: dayTopic,
            teacher_id: user.id
          })
        });
        const data = await res.json();
        if (!data.job_id) throw new Error('No job ID returned');

        // Poll until complete
        let complete = false;
        let attempts = 0;
        while (!complete && attempts < 120 && !cancelledRef.current) {
          await new Promise(r => setTimeout(r, 3000));
          attempts++;
          try {
            const statusRes = await fetch(`/api/study-notes/generate/status/${data.job_id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const statusData = await statusRes.json();

            if (statusData.status === 'complete') {
              const noteId = statusData.note_id || statusData.result_id;
              setNotesProgress(prev => ({ ...prev, [day.day]: { status: 'complete', noteId } }));
              complete = true;
            } else if (statusData.status === 'error') {
              setNotesProgress(prev => ({ ...prev, [day.day]: { status: 'error', error: statusData.error } }));
              complete = true;
            } else {
              setNotesProgress(prev => ({ ...prev, [day.day]: { status: 'generating', progress: statusData.progress || 'Working...' } }));
            }
          } catch {
            // Polling error, retry
          }
        }
        if (!complete && !cancelledRef.current) {
          setNotesProgress(prev => ({ ...prev, [day.day]: { status: 'error', error: 'Timed out' } }));
        }
      } catch (err) {
        setNotesProgress(prev => ({ ...prev, [day.day]: { status: 'error', error: err.message } }));
      }
    }

    setGeneratingNotes(false);
    if (!cancelledRef.current) {
      toast.success('Study notes generated for all days!');
    }
  }, [subject]);

  // Generate full week plan
  const generatePlan = async () => {
    setIsGenerating(true);
    
    const topic = getWeekTopic(selectedYear, selectedWeek, selectedTerm);
    const weekData = getWeekData(selectedYear, selectedWeek, selectedTerm);
    
    // Use learning outcomes from weekly lessons data if available
    const learningOutcomes = weekData?.learningOutcomes || [
      `Understand the fundamentals of ${topic}`,
      `Apply ${topic} concepts to practical situations`,
      `Analyze and evaluate ${topic} in context`,
      `Create solutions using ${topic} knowledge`,
      `Demonstrate mastery of ${topic}`
    ];
    
    // Generate lessons for all 5 days
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    const generatedDays = days.map((day, index) => 
      generateDayLesson(day, index + 1, topic, learningOutcomes, weekData)
    );
    
    setGeneratedPlan({
      year: selectedYear,
      term: selectedTerm,
      week: selectedWeek,
      topic: topic,
      subject: subject,
      strand: weekData?.strand || '',
      days: generatedDays
    });
    
    toast.success(`${subject} lesson plans generated for Monday through Friday!`);
    setIsGenerating(false);

    // Auto-generate study notes if toggle is on
    if (includeStudyNotes) {
      setNotesProgress({});
      const yearLevel = parseInt(selectedYear) || 11;
      generateStudyNotesForDays(generatedDays, yearLevel);
    }
  };

  const printPlan = () => {
    if (!generatedPlan) return;

    const printWindow = window.open('', '_blank');
    const { topic, days } = generatedPlan;

    const colorMap = {
      'green': { primary: '#059669', light: '#d1fae5', text: '#065f46' },
      'blue': { primary: '#2563eb', light: '#dbeafe', text: '#1e40af' },
      'purple': { primary: '#7c3aed', light: '#ede9fe', text: '#5b21b6' },
      'red': { primary: '#dc2626', light: '#fee2e2', text: '#991b1b' },
      'amber': { primary: '#d97706', light: '#fef3c7', text: '#92400e' },
      'teal': { primary: '#0d9488', light: '#ccfbf1', text: '#115e59' },
      'indigo': { primary: '#4f46e5', light: '#e0e7ff', text: '#3730a3' },
      'pink': { primary: '#db2777', light: '#fce7f3', text: '#9d174d' }
    };
    
    const colors = colorMap[subjectColor] || colorMap['green'];

    const dayHtml = days.map((day, index) => `
      <div class="day-section ${index > 0 ? 'page-break' : ''}">
        <div class="day-header">
          <div class="day-badge">${day.day}</div>
          <div class="day-title">
            <span class="generated-badge">✅ GENERATED LESSON</span>
          </div>
        </div>
        
        <table class="lesson-table">
          <tr><th width="20%">Lesson Title</th><td>${day.title}</td></tr>
          <tr><th>Duration</th><td>${day.duration}</td></tr>
          <tr><th>Learning Objectives</th><td><ul>${day.objectives.map(obj => `<li>${obj}</li>`).join('')}</ul></td></tr>
          <tr><th>Introduction</th><td class="intro-cell">${day.introduction}</td></tr>
          <tr><th>Main Activities</th><td>
            <table class="activities-table">
              <tr><th>Activity</th><th>Time</th><th>Description</th></tr>
              ${day.activities.map(act => `<tr><td>${act.name}</td><td>${act.duration}</td><td>${act.description}</td></tr>`).join('')}
            </table>
          </td></tr>
          <tr><th>Materials</th><td>${day.materials}</td></tr>
          <tr><th>Assessment</th><td>${day.assessment}</td></tr>
          <tr><th>Homework</th><td>${day.homework}</td></tr>
        </table>
      </div>
    `).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Weekly Lesson Plan - Year ${generatedPlan.year} ${subject}</title>
        <style>
          * { box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 900px; margin: 0 auto; }
          .header { background: linear-gradient(135deg, ${colors.primary}, ${colors.primary}dd); color: white; padding: 20px; border-radius: 12px; margin-bottom: 25px; }
          .header h1 { margin: 0 0 5px 0; font-size: 22px; }
          .header-info { display: flex; gap: 15px; margin-top: 15px; flex-wrap: wrap; }
          .header-badge { background: rgba(255,255,255,0.2); padding: 5px 12px; border-radius: 15px; font-size: 13px; }
          .week-overview { background: ${colors.light}; border: 2px solid ${colors.primary}; border-radius: 10px; padding: 15px; margin-bottom: 25px; }
          .week-overview h3 { margin: 0 0 10px 0; color: ${colors.text}; }
          .day-section { margin-bottom: 30px; }
          .page-break { page-break-before: always; }
          .day-header { display: flex; align-items: center; gap: 15px; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 3px solid ${colors.primary}; }
          .day-badge { background: ${colors.primary}; color: white; padding: 8px 16px; border-radius: 20px; font-weight: bold; font-size: 14px; }
          .generated-badge { background: ${colors.light}; color: ${colors.text}; padding: 4px 10px; border-radius: 5px; font-size: 12px; font-weight: bold; }
          .lesson-table { width: 100%; border-collapse: collapse; font-size: 13px; }
          .lesson-table th, .lesson-table td { border: 1px solid #d1d5db; padding: 10px; text-align: left; vertical-align: top; }
          .lesson-table th { background: #f3f4f6; font-weight: bold; width: 20%; }
          .lesson-table ul { margin: 5px 0; padding-left: 20px; }
          .activities-table { width: 100%; border-collapse: collapse; font-size: 12px; }
          .activities-table th, .activities-table td { border: 1px solid #e5e7eb; padding: 6px; }
          .activities-table th { background: ${colors.light}; }
          .intro-cell { font-style: italic; line-height: 1.5; }
          .footer { text-align: center; padding: 20px; background: #f8fafc; border-radius: 8px; margin-top: 30px; font-size: 12px; color: #64748b; }
          @media print { .page-break { page-break-before: always; } body { padding: 10px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>📚 Weekly Lesson Plan (Mon-Fri)</h1>
          <p>Year ${generatedPlan.year} ${subject}</p>
          <div class="header-info">
            <span class="header-badge">📅 Term ${generatedPlan.term.replace('term', '')} - Week ${generatedPlan.week}</span>
            <span class="header-badge">📖 ${topic}</span>
            <span class="header-badge">✨ All 5 Days Generated</span>
          </div>
        </div>
        <div class="week-overview">
          <h3>📋 Week Topic: ${topic}</h3>
          <p><strong>Subject:</strong> ${subject}</p>
          <p><strong>Year Level:</strong> Year ${generatedPlan.year}</p>
        </div>
        ${dayHtml}
        <div class="footer">
          <p>🌴 Penama Education Portal - ${subject} Curriculum | Generated ${new Date().toLocaleDateString()}</p>
          <p>Teacher: _________________________ | Signature: _________________________</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  // Share lesson plan to Academic Principal for approval
  const shareToAcademic = async () => {
    if (!generatedPlan) return;
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    if (!currentUser?.school_id) {
      toast.error('School ID not found. Please log out and log in again.');
      return;
    }
    setIsSharingToAcademic(true);
    try {
      const response = await fetch(`/api/academic/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teacher_id: currentUser.id,
          teacher_name: currentUser.name || 'Unknown Teacher',
          school_id: currentUser.school_id,
          submission_type: 'lesson_plan',
          title: `Lesson Plan: Year ${generatedPlan.year} ${subject} - Term ${generatedPlan.term.replace('term', '')} Week ${generatedPlan.week}`,
          subject: subject,
          year_level: parseInt(generatedPlan.year),
          term: parseInt(generatedPlan.term.replace('term', '')),
          content: generatedPlan
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Lesson plan submitted to Academic Principal for approval!');
      } else {
        toast.error(data.detail || 'Failed to submit. Please try again.');
      }
    } catch (err) {
      console.error('Share to academic error:', err);
      toast.error('Failed to submit to Academic. Please try again.');
    } finally {
      setIsSharingToAcademic(false);
    }
  };

  // Color classes based on subject
  const colorClasses = {
    'green': { border: 'border-green-200', bg: 'from-green-50 to-teal-50', button: 'bg-green-600 hover:bg-green-700', badge: 'bg-green-100 text-green-700', card: 'bg-green-50' },
    'blue': { border: 'border-blue-200', bg: 'from-blue-50 to-indigo-50', button: 'bg-blue-600 hover:bg-blue-700', badge: 'bg-blue-100 text-blue-700', card: 'bg-blue-50' },
    'purple': { border: 'border-purple-200', bg: 'from-purple-50 to-violet-50', button: 'bg-purple-600 hover:bg-purple-700', badge: 'bg-purple-100 text-purple-700', card: 'bg-purple-50' },
    'red': { border: 'border-red-200', bg: 'from-red-50 to-rose-50', button: 'bg-red-600 hover:bg-red-700', badge: 'bg-red-100 text-red-700', card: 'bg-red-50' },
    'amber': { border: 'border-amber-200', bg: 'from-amber-50 to-yellow-50', button: 'bg-amber-600 hover:bg-amber-700', badge: 'bg-amber-100 text-amber-700', card: 'bg-amber-50' },
    'teal': { border: 'border-teal-200', bg: 'from-teal-50 to-cyan-50', button: 'bg-teal-600 hover:bg-teal-700', badge: 'bg-teal-100 text-teal-700', card: 'bg-teal-50' },
    'indigo': { border: 'border-indigo-200', bg: 'from-indigo-50 to-blue-50', button: 'bg-indigo-600 hover:bg-indigo-700', badge: 'bg-indigo-100 text-indigo-700', card: 'bg-indigo-50' },
    'pink': { border: 'border-pink-200', bg: 'from-pink-50 to-rose-50', button: 'bg-pink-600 hover:bg-pink-700', badge: 'bg-pink-100 text-pink-700', card: 'bg-pink-50' }
  };
  
  const colors = colorClasses[subjectColor] || colorClasses['green'];

  return (
    <Card className={`border-2 ${colors.border}`}>
      <CardHeader className={`bg-gradient-to-r ${colors.bg}`}>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-gray-700" />
          {subject} Lesson Plan Generator (Full Week)
        </CardTitle>
        <CardDescription>
          Generate complete lesson plans for Monday through Friday
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        {/* Selection Controls */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Year Level</label>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger>
                <SelectValue placeholder="Select Year" />
              </SelectTrigger>
              <SelectContent>
                {allowedYears.map(year => (
                  <SelectItem key={year} value={year}>Year {year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Term</label>
            <Select value={selectedTerm} onValueChange={setSelectedTerm}>
              <SelectTrigger>
                <SelectValue placeholder="Select Term" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="term1">Term 1</SelectItem>
                <SelectItem value="term2">Term 2</SelectItem>
                <SelectItem value="term3">Term 3</SelectItem>
                <SelectItem value="term4">Term 4</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Week</label>
            <Select value={selectedWeek} onValueChange={setSelectedWeek}>
              <SelectTrigger>
                <SelectValue placeholder="Select Week" />
              </SelectTrigger>
              <SelectContent>
                {(() => {
                  const weeksData = getWeeklyLessonsData(selectedYear, selectedTerm);
                  const numWeeks = weeksData.length > 0 ? weeksData.length : (subjectTopics[subject]?.[parseInt(selectedYear)]?.length || 10);
                  return Array.from({length: numWeeks}, (_, i) => i + 1).map(w => (
                    <SelectItem key={w} value={w.toString()}>Week {w}</SelectItem>
                  ));
                })()}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Duration</label>
            <Select value={selectedDuration} onValueChange={setSelectedDuration}>
              <SelectTrigger data-testid="select-duration">
                <SelectValue placeholder="Duration" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({length: 28}, (_, i) => 15 + i * 5).map(min => (
                  <SelectItem key={min} value={min.toString()}>{min} min</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-end">
            <Button 
              onClick={generatePlan}
              className={`w-full ${colors.button}`}
              disabled={isGenerating || generatingNotes}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Plan
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Study Notes Toggle */}
        <div className="mb-6">
          <label 
            className="flex items-center gap-3 cursor-pointer select-none group"
            data-testid="include-study-notes-toggle"
          >
            <div 
              className={`relative w-11 h-6 rounded-full transition-colors ${includeStudyNotes ? 'bg-emerald-500' : 'bg-gray-300'}`}
              onClick={() => setIncludeStudyNotes(!includeStudyNotes)}
            >
              <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${includeStudyNotes ? 'translate-x-5' : ''}`} />
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 flex items-center gap-1.5">
                <NotebookPen className="w-4 h-4" />
                Also generate Study Notes for each day
              </span>
              <span className="text-xs text-gray-500">
                AI-powered notes with diagrams & practice questions — auto-saved to AI Teacher Hub
              </span>
            </div>
          </label>
        </div>
        
        {/* Week Preview */}
        <div className={`${colors.card} rounded-lg p-4 mb-6`}>
          <h4 className="font-semibold text-gray-800 flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            Week {selectedWeek}: {getWeekTopic(selectedYear, selectedWeek)}
          </h4>
          <p className="text-sm text-gray-600 mt-1">{subject} | Year {selectedYear} | Term {selectedTerm.replace('term', '')}</p>
        </div>
        
        {/* Generated Plan Preview */}
        {generatedPlan && (
          <div className={`border-2 ${colors.border} rounded-lg overflow-hidden`}>
            <div className={`${colors.card} p-4 flex items-center justify-between`}>
              <div>
                <h4 className="font-semibold text-gray-800">
                  ✅ Lesson Plan Generated!
                </h4>
                <p className="text-sm text-gray-600">
                  Year {generatedPlan.year} | Term {generatedPlan.term.replace('term', '')} | Week {generatedPlan.week}
                </p>
              </div>
              <div className="flex gap-2">
                <Button onClick={printPlan} className={colors.button} data-testid="print-lesson-plan-btn">
                  <Printer className="w-4 h-4 mr-2" />
                  Print Lesson Plan
                </Button>
                <Button 
                  onClick={shareToAcademic} 
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  disabled={isSharingToAcademic}
                  data-testid="share-to-academic-lesson-btn"
                >
                  {isSharingToAcademic ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Share2 className="w-4 h-4 mr-2" />
                  )}
                  Share to Academic
                </Button>
              </div>
            </div>
            
            <div className="p-4">
              <div className="grid grid-cols-5 gap-2">
                {generatedPlan.days.map((day) => (
                  <div 
                    key={day.day}
                    className={`p-3 rounded-lg text-center ${colors.card} border-2 ${colors.border}`}
                  >
                    <div className="font-bold text-sm text-gray-700">
                      {day.day}
                    </div>
                    <div className="text-xs mt-1 text-gray-600">
                      ✅ Generated
                    </div>
                  </div>
                ))}
              </div>
              
              <div className={`mt-4 p-3 ${colors.card} rounded-lg`}>
                <p className="text-sm text-gray-800">
                  <strong>All 5 days generated!</strong> Click &quot;Print Lesson Plan&quot; to get your complete Monday-Friday lesson plans.
                </p>
              </div>

              {/* Study Notes Progress */}
              {(generatingNotes || Object.keys(notesProgress).length > 0) && (
                <div className="mt-4 border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                    <NotebookPen className="w-4 h-4" />
                    Study Notes Generation
                    {generatingNotes && <Loader2 className="w-3 h-3 animate-spin text-emerald-600" />}
                  </h4>
                  <div className="grid grid-cols-5 gap-2">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map(day => {
                      const progress = notesProgress[day];
                      if (!progress) return (
                        <div key={day} className="p-2 rounded-lg bg-gray-50 border text-center">
                          <div className="text-xs font-medium text-gray-500">{day}</div>
                          <div className="text-xs text-gray-400 mt-1">Queued</div>
                        </div>
                      );
                      return (
                        <div key={day} className={`p-2 rounded-lg border text-center ${
                          progress.status === 'complete' ? 'bg-emerald-50 border-emerald-200' :
                          progress.status === 'error' ? 'bg-red-50 border-red-200' :
                          'bg-blue-50 border-blue-200'
                        }`}>
                          <div className="text-xs font-medium text-gray-700">{day}</div>
                          {progress.status === 'generating' && (
                            <div className="flex items-center justify-center gap-1 mt-1">
                              <Loader2 className="w-3 h-3 animate-spin text-blue-500" />
                              <span className="text-xs text-blue-600 truncate">{progress.progress}</span>
                            </div>
                          )}
                          {progress.status === 'complete' && (
                            <>
                              <div className="text-xs text-emerald-600 mt-1">Done</div>
                              {progress.noteId && (
                                <button
                                  onClick={() => navigate('/dashboard/ai-planner')}
                                  className="text-xs text-emerald-700 underline mt-0.5 hover:text-emerald-800 flex items-center justify-center gap-0.5"
                                  data-testid={`view-note-${day.toLowerCase()}`}
                                >
                                  <ExternalLink className="w-3 h-3" /> View
                                </button>
                              )}
                            </>
                          )}
                          {progress.status === 'error' && (
                            <div className="text-xs text-red-600 mt-1 truncate" title={progress.error}>Failed</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {!generatingNotes && Object.values(notesProgress).every(p => p.status === 'complete') && (
                    <div className="mt-3 p-2 bg-emerald-50 rounded-lg text-center">
                      <p className="text-sm text-emerald-700">
                        All study notes ready! Find them in <button onClick={() => navigate('/dashboard/ai-planner')} className="underline font-semibold hover:text-emerald-800">AI Teacher Hub</button>
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        
        {!generatedPlan && (
          <div className="text-center py-8 text-gray-500">
            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Select Year, Term, and Week to generate lesson plans for all 5 days</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LessonPlanGenerator;
